import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Stack, Step, StepLabel, Stepper } from "@mui/material";
import InfoSection from "./Component/InfoSection";
import ConfigSection from "./Component/ConfigSection";
import ProcessorSection from "./Component/ProcessorSection";
import AgentAssignmentSection from "./Component/AgentAssignmentSection";
import { useTranslation } from "react-i18next";
import { queryDropdownOptions } from "../offline-merchant-detail/model";
import { addMdrMerchant, createMerchant, onboardProcessors, validateMcc } from "./model";
import useSnackbarMAS from "utils/useSnackbarMAS/useSnackbarMAS";
import merchantManagement from "routes/merchantManagement";
import { formPreventEnterSubmit, shiftDecimal } from "utils/utils";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import usePageState from "utils/usePageState";
import usePageWrapper from "utils/usePageWrapper/usePageWrapper";

/** @param {import("./Component/ProcessorTable").processorFormValue} processorFormValue */
const getMccValidationPayload = (processorFormValue) => {
  return Object.entries(processorFormValue).flatMap(([cardTypeKey, cardTypeValue]) => {
    let gateways = Object.entries(cardTypeValue.gateways)
      .filter(([, gatewayValue]) => gatewayValue.enabled)
      .map(
        ([gatewayKey, gatewayValue]) => ({
          cardType: cardTypeKey,
          processor: gatewayKey,
          ...gatewayValue.businessPlan && { businessPlan: gatewayValue.businessPlan },
          ...gatewayValue.mcc && { mcc: gatewayValue.mcc },
        }));

    // Only keep the gateway entires that have MCC
    gateways = gateways.filter(({ mcc }) => mcc);

    return gateways;
  })
}

/** @param {import("./Component/ProcessorTable").processorFormValue} processorFormValue */
const getMdrListFromForm = (processorFormValue) => {
  /** @param {string} s */
  const handleNumString = (s) => shiftDecimal(Number(s) || 0, -2) // prevent floating point error

  return Object.entries(processorFormValue).map(([cardTypeKey, cardTypeValue]) => {
    let gateways = Object.entries(cardTypeValue.gateways).map(
      ([gatewayKey, gatewayValue]) => ({
        cardType: cardTypeKey,
        ...gatewayValue.businessPlan && { businessPlan: gatewayValue.businessPlan },
        paymentGateway: gatewayKey,
        cost: 0,
        ...gatewayValue.mcc && { mcc: gatewayValue.mcc },
        enabled: gatewayValue.enabled,
        inUse: gatewayKey === cardTypeValue.inUse
      }));

    // Special handling for OCTOPUS and AMEX, see MAS-673
    switch (cardTypeKey) {
      case "AMEX": {
        const enabledGateway = gateways.filter(({ enabled }) => enabled);
        if (enabledGateway.length === 1 && enabledGateway[0].paymentGateway === "AMEX") {
          gateways = enabledGateway;
        } else {
          // Default create AMEX_BBMSL GSI, since it has business plan and MCC value
          gateways = gateways.filter((gateawy) => gateawy.paymentGateway === "AMEX_BBMSL");
        }
        break;
      };
      case "OCTOPUS": {
        const enabledGateway = gateways.filter(({ enabled }) => enabled);
        const haveMdrOrCommission = cardTypeValue.domesticMdr || cardTypeValue.foreignMdr || cardTypeValue.referrerCommission;
        if (enabledGateway.length === 1) {
          gateways = enabledGateway;
        } else if (haveMdrOrCommission) {
          gateways = gateways.filter((gateawy) => gateawy.paymentGateway === "OCTOPUS_BBMSL");
        } else {
          // If no gateway is enabled and no MDR or commission is set, return null to indicate that this card type should be skipped in the final payload
          return null;
        }
        break;
      }
    }

    return {
      cardType: cardTypeKey,
      mdr: handleNumString(cardTypeValue.domesticMdr),
      foreignMdr: handleNumString(cardTypeValue.foreignMdr),
      referrerCommission: handleNumString(cardTypeValue.referrerCommission),
      gatewaySwitchingInfoList: gateways
    }
  }).filter(Boolean) // Remove null entries for OCTOPUS when no gateway is enabled and no MDR or commission is set
}

/** @param {import("./Component/ProcessorTable").processorFormValue} processorFormValue */
const getPreferenceFromForm = (processorFormValue) => {
  return JSON.stringify({
    gatewayMap: {
      MASKED: "NONE",
      JCB: processorFormValue?.JCB?.inUse || "NONE",
      VERVE: "NONE",
      ALIPAY: processorFormValue?.ALIPAY?.inUse || "NONE",
      AMEX: processorFormValue?.AMEX?.inUse || "NONE",
      LOYALTY: "NONE",
      DISCOVER: "NONE",
      MASTER: processorFormValue?.MASTER?.inUse || "NONE",
      DINERS: "NONE",
      TNG: "NONE",
      VISA: processorFormValue?.VISA?.inUse || "NONE",
      UNIONPAY: processorFormValue?.UNIONPAY?.inUse || "NONE",
      WECHAT: processorFormValue?.WECHAT?.inUse || "NONE",
      OCTOPUS: processorFormValue?.OCTOPUS?.inUse || "NONE",
      PAYME: processorFormValue?.PAYME?.inUse || "NONE",
    },
    businessRules: null,
    partialAuthIndicator: null,
    networkOrder: null,
  })
}

/** @param {import("./Component/ProcessorTable").processorFormValue} processorFormValue */
const getOnboardProcessorsFromForm = (processorFormValue) => {
  // Obtain array of enabled gateways wihtout duplicates
  const enabledGateways = [...new Set(Object.values(processorFormValue).flatMap(({ gateways }) => {
    return Object.entries(gateways).map(([gatewayKey, gatewayValue]) => {
      return gatewayValue.enabled ? gatewayKey : null
    })
  }).filter(Boolean)).values()];

  // Full list:  ["ALLINPAY", "FUBON", "YEDPAY", "AMEX_BBMSL", "AMEX", "OCTOPUS", "OCTOPUS_BBMSL", "OCTOPUS_HKBN", "WECHAT_PAY", "ALIPAY", "HSBC"]
  // MAS-673, removed AMEX_BBMSL. It will not be onboarded during merchant creation.
  const ONBOARD_ORDER = ["ALLINPAY", "FUBON", "YEDPAY", "WECHAT_PAY", "ALIPAY", "HSBC"];
  return ONBOARD_ORDER.filter((gateway) => enabledGateways.includes(gateway));
}

const PAGE_LOCALE_KEY = "page.merchants.addMerchant.";

// Alipay config
const PARTNER_ID = "2088031571574577";
const PARTNER_KEY = "yteulb5zxa47h99k8imkoplecxcey738";

const AddOfflineMerchant = ({ path, redirectFn }) => {
  const { t } = useTranslation();
  const { snackbarSuccess, snackbarError } = useSnackbarMAS();
  const { pageState } = usePageState({ pagePath: path });
  const formProps = useForm({
    defaultValues: {
      shareholderInfo: [{ name: "", idNo: "", percentage: "0" }]
    },
    mode: "onChange",
  });
  const { handleSubmit, trigger, setError } = formProps;
  const { callApis, PageWrapper, PageOuterContainer, PageStickyHeader, PageBody, PageStickyFooter, scrollToHeader } = usePageWrapper();

  const steps = useMemo(
    () => [
      {
        key: "agentAssignment",
        label: t(`${PAGE_LOCALE_KEY}agentAssignment.title`, {
          defaultValue: "Assign Agent",
        }),
      },
      {
        key: "info",
        label: t("page.merchants.merchantDetail.info.title", {
          defaultValue: "Info",
        }),
      },
      {
        key: "config",
        label: t("page.merchants.merchantDetail.config.title", {
          defaultValue: "Config",
        }),
      },
      {
        key: "processor",
        label: t("page.merchants.merchantDetail.processorMdr.title", {
          defaultValue: "Payment Gateway Switching & MDR",
        }),
      },
    ],
    [t],
  );

  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState(undefined);

  const isLoading = isSubmitting || isValidating;
  const defaultAgent = useMemo(
    () => ({
      isoAgentId: pageState?.isoAgentId || pageState?.id || "",
      email: pageState?.isoAgentEmail || "",
    }),
    [pageState],
  );

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [dropdownOptionsResp] = await callApis([{
          api: queryDropdownOptions,
        }]);
        console.log("data", dropdownOptionsResp.data);
        if (!dropdownOptionsResp.isSuccess) {
          dropdownOptionsResp.isError && snackbarError({
            message: tLocal("toast.getDropdownOptions.failed"),
            durationInMs: 5000
          });
        } else {
          setDropdownOptions({
            serviceIntegratorList: dropdownOptionsResp.data?.serviceIntegratorList,
            referrerList: dropdownOptionsResp.data?.referrerList,
            salesList: dropdownOptionsResp.data?.salesList,
            mccList: (dropdownOptionsResp.data?.mccList ?? []).map((mccObj) => ({
              key: mccObj.key,
              title: mccObj.title,
              value: mccObj.key,
            })),
          });
        }
      } catch (e) {
        console.error("Failed to fetch dropdown options", e);
      }
    };
    fetchOptions();
  }, []);

  const next = async () => {
    const currentStep = steps[activeStep]?.key;
    const valid = await trigger(
      currentStep === "agentAssignment" ? ["isoAgentEmail"] : undefined,
      { shouldFocus: true },
    );
    if (!valid) return;
    setActiveStep((s) => Math.min(s + 1, steps.length - 1));
    scrollToHeader();
  };

  const back = () => {
    setActiveStep((s) => Math.max(0, s - 1))
    scrollToHeader();
  };

  const onSubmit = async (formValue) => {
    const valid = await trigger(undefined, { shouldFocus: true });
    if (!valid) return;

    try {
      setIsValidating(true);

      const mccValidationPayload = getMccValidationPayload(formValue.processors);
      const mccValidationParam = {
        mccList: mccValidationPayload
      }

      const [validateMccResp] = await callApis([{
        api: validateMcc,
        payload: mccValidationParam,
        allowedResCodes: ["0000", "3043"],
        blockOnError: false,
        blockOnUnauthorized: false,
      }])
      console.log("validateMccResp", validateMccResp);

      if (validateMccResp?.responseCode === "3043") {
        snackbarError({
          message: t(PAGE_LOCALE_KEY + "fetchMessage.validateMccFailed"),
          durationInMs: 5000
        });

        const failedMccList = validateMccResp?.data?.filter((item) => !item.success)
        console.log("failedMccList", failedMccList);

        failedMccList.forEach((item, index) => {
          const fieldMccKey = `processors.${item.param.cardType}.gateways.${item.param.processor}.mcc`;

          if (index === 0) {
            // Scroll to the first error
            const element = document.querySelector(`[name="${fieldMccKey}"]`)
            element?.scrollIntoView({ behavior: "smooth", block: "center" });
          }
          setError(fieldMccKey, {
            type: "manual",
            message: item.message
          });
        });
        setIsValidating(false);
        return;
      }

      setIsValidating(false);
      setIsSubmitting(true);

      const { isKDollar, ...otherFields } = formValue;
      const campaign = [isKDollar && "KDOLLAR"].filter(Boolean);

      const mdrList = getMdrListFromForm(formValue.processors);
      const preference = getPreferenceFromForm(formValue.processors);
      const enabledGateways = getOnboardProcessorsFromForm(formValue.processors);
      delete otherFields.processors;

      const fubonMasterProcessor = formProps.getValues("processors.MASTER.gateways.FUBON");
      const fubonVisaProcessor = formProps.getValues("processors.VISA.gateways.FUBON");

      const createMerchantParam = {
        ...otherFields,
        otherInfo: { campaign },
        mdrList: mdrList,
        preference: preference,

        // Fubon bank fields
        isFubonBank: fubonMasterProcessor.enabled || fubonVisaProcessor.enabled,
        masterBusinessPlan: fubonMasterProcessor.businessPlan,
        visaBusinessPlan: fubonVisaProcessor.businessPlan,

        // ALIPAY fields
        partnerId: PARTNER_ID,
        partnerKey: PARTNER_KEY,

        // isGatewaySwitchingAvailable is now always true since new merchants won't be using DSB/DBS/globalPayments
        isGatewaySwitchingAvailable: true,
      }

      const [createMerchantResp] = await callApis([{
        api: createMerchant,
        payload: createMerchantParam,
        blockOnError: false,
        blockOnUnauthorized: false,
      }])
      console.log("createMerchantResp", createMerchantResp);

      if (!createMerchantResp.isSuccess) {
        createMerchantResp.isError && snackbarError({
          message: t(PAGE_LOCALE_KEY + "fetchMessage.addMerchantFailed"),
          durationInMs: 5000
        });
        return;
      }

      const respData = createMerchantResp.data;
      const addMdrMerchantParam = {
        merchantId: respData.merchantId,
        merchantName: formValue.name,
        bankAccount: formValue.bankAccount,
        beneficiary: formValue.beneficiary,
        settleCycle: formValue.settleCycle,
        email: formValue.email,
        bankAccountRemarks: formValue.bankAccountRemarks,
        referrer: formValue.referrer
          ? {
            id: formValue.referrer,
          }
          : null,
        sales: formValue.sales
          ? {
            id: formValue.sales,
          }
          : null,
        onboardSales: formValue.sales
          ? {
            id: formValue.sales,
          }
          : null,
        zeroPlan: formValue.zeroPlan,
        autoApproved: formValue.autoApproved,

        mdrList: mdrList,
      }

      const [addMdrMerchantResp] = await callApis([{
        api: addMdrMerchant,
        payload: addMdrMerchantParam,
        blockOnError: false,
        blockOnUnauthorized: false,
      }])
      console.log("addMdrMerchantResp", addMdrMerchantResp);
      if (!addMdrMerchantResp.isSuccess) {
        //TODO: Might need to add handling for merchant entity created but merchant info create fail

        addMdrMerchantResp.isError && snackbarError({
          message: t(PAGE_LOCALE_KEY + "fetchMessage.addMerchantMdrFailed"),
          durationInMs: 5000
        });
        return;
      }

      const onboardProcessorsParam = {
        merchantId: respData.merchantId,
        processors: enabledGateways,
      }

      const [onboardProcessorsResp] = await callApis([{
        api: onboardProcessors,
        payload: onboardProcessorsParam,
        blockOnError: false,
        blockOnUnauthorized: false,
      }])
      console.log("onboardProcessorsResp", onboardProcessorsResp);
      if (!onboardProcessorsResp.isSuccess) {
        // Proceed to merchant detail page even if onboarding external processors failed
        onboardProcessorsResp.isError && snackbarError({
          message: t(PAGE_LOCALE_KEY + "fetchMessage.onboardProcessorsAccountFailed"),
          durationInMs: 5000
        });
      }

      snackbarSuccess({
        message: t(PAGE_LOCALE_KEY + "fetchMessage.createMerchantSuccess"),
        durationInMs: 5000
      });

      const merchantDetailPath = "/merchant-management/merchants/offline-merchant-detail";
      const redirectPayload = {
        name:
          merchantManagement
            .find((tab) => tab.tabName === "Merchants")
            .page.find((page) => page.path === merchantDetailPath).name + respData.merchantId,
        path: merchantDetailPath,
        state: {
          merchantId: respData.merchantId,
          redirectFn: redirectFn,
        },
      };
      redirectFn("redirectPage", redirectPayload);
      redirectFn("close", { tabId: path });
    } catch (error) {
      console.error("Create Merchant error:", error);
      snackbarError({
        message: `${t(PAGE_LOCALE_KEY + "fetchMessage.createMerchantFailed")}: ${error.message}`,
        durationInMs: 5000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageOuterContainer>
      <PageWrapper noPadding isLoading={isSubmitting}>
        <PageStickyHeader noSticky>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              "& .MuiStepIcon-root.Mui-active": { color: "rgb(54, 180, 191)" },
              "& .MuiStepIcon-root.Mui-completed": { color: "rgb(54, 180, 191)" },
            }}
          >
            {steps.map((s) => (
              <Step key={s.key}>
                <StepLabel>{s.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </PageStickyHeader>

        <form noValidate onSubmit={handleSubmit(onSubmit)} onKeyDown={formPreventEnterSubmit}>
          <PageBody>
            {activeStep === 0 && (
              <AgentAssignmentSection
                formProps={formProps}
                isLoading={isLoading}
                defaultAgent={defaultAgent}
                redirectFn={redirectFn}
              />
            )}

            {activeStep === 1 && (
              <InfoSection
                formProps={formProps}
                isLoading={isLoading}
                mccDropdownOptions={dropdownOptions?.mccList}
                dropdownOptions={dropdownOptions}
              />
            )}

            {activeStep === 2 && (
              <ConfigSection
                formProps={formProps}
                isEdit
                isLoading={isLoading}
              />
            )}

            {activeStep === 3 && (
              <ProcessorSection
                formProps={formProps}
                isLoading={isLoading}
                mccDropdownOptions={dropdownOptions?.mccList}
              />
            )}

            <PageStickyFooter>
              <Stack
                direction="row"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={2}
              >
                <ButtonMAS
                  variant="bordered"
                  disabled={activeStep === 0 || isLoading}
                  sx={{ visibility: activeStep === 0 ? "hidden" : "visible" }}
                  onClick={back}
                  type="button"
                >
                  {t("general.button.previous", { defaultValue: "Previous" })}
                </ButtonMAS>
                {activeStep < steps.length - 1 ? (
                  <ButtonMAS
                    variant="primary"
                    onClick={next}
                    type="button"
                    disabled={isLoading}
                  >
                    {t("general.button.next", { defaultValue: "Next" })}
                  </ButtonMAS>
                ) : (
                  <ButtonMAS
                    isLoading={isValidating}
                    variant="primary"
                    type="submit"
                  >
                    {t("general.button.submit", { defaultValue: "Submit" })}
                  </ButtonMAS>
                )}
              </Stack>
            </PageStickyFooter>
          </PageBody>
        </form>
      </PageWrapper>
    </PageOuterContainer>
  );
};

export default React.memo(AddOfflineMerchant);
